import { apiClient } from "./client";
import type { PeriodFilter } from "../../pages/reports/helpers";
import {
    ONLINE_STORE_LABEL,
    type OnlineCockpitResponse,
    type RetailAvgItemsResponse,
    type RetailDefectsResponse,
    type RetailFrequencyResponse,
    type RetailHourlyRevenueResponse,
    type RetailLastSalesResponse,
    type RetailPricingMixResponse,
    type RetailStockGroupNode,
    type RetailStockGroupsResponse,
} from "./onlineCockpitTypes";

export * from "./onlineCockpitTypes";
export * from "./onlineCockpitPromotionsApi";

function periodStoreParams(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}) {
    return {
        period: params.period,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        store: params.store ?? ONLINE_STORE_LABEL,
    };
}

export async function fetchOnlineCockpitReport(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
    revenueWithStorno?: boolean;
    ordersWithStorno?: boolean;
}) {
    const { data } = await apiClient.get<OnlineCockpitResponse>("/reports/online-cockpit", {
        params: {
            period: params.period,
            date_from: params.dateFrom,
            date_to: params.dateTo,
            revenue_storno: params.revenueWithStorno === false ? 0 : 1,
            orders_storno: params.ordersWithStorno === false ? 0 : 1,
        },
    });

    return data;
}

export async function fetchOnlineHourlyRevenueReport(params: {
    referenceDate?: string;
    store?: string;
    period?: PeriodFilter;
    dateFrom?: string;
    dateTo?: string;
}) {
    const { data } = await apiClient.get<RetailHourlyRevenueResponse>("/reports/online-hourly-revenue", {
        params: {
            reference_date: params.referenceDate,
            store: params.store ?? ONLINE_STORE_LABEL,
            period: params.period,
            date_from: params.dateFrom,
            date_to: params.dateTo,
        },
    });

    return data;
}

export async function fetchOnlineAvgItemsReport(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}) {
    const { data } = await apiClient.get<RetailAvgItemsResponse>(
        "/reports/online-avg-items",
        { params: periodStoreParams(params) },
    );

    return data;
}

export async function fetchOnlineDefectsReport(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}) {
    const { data } = await apiClient.get<RetailDefectsResponse>("/reports/online-defects", {
        params: periodStoreParams(params),
        timeout: 180_000,
    });

    return data;
}

export async function fetchOnlinePricingMixReport(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}) {
    const { data } = await apiClient.get<RetailPricingMixResponse>(
        "/reports/online-pricing-mix",
        { params: periodStoreParams(params) },
    );

    return data;
}

export async function fetchOnlineLastSalesReport(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
    search?: string;
    page?: number;
    perPage?: number;
}) {
    const { data } = await apiClient.get<RetailLastSalesResponse>("/reports/online-last-sales", {
        params: {
            ...periodStoreParams(params),
            search: params.search,
            page: params.page,
            per_page: params.perPage,
        },
        timeout: 180_000,
    });

    return data;
}

export async function fetchOnlineStockGroupsReport(params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}) {
    const { data } = await apiClient.get<RetailStockGroupsResponse>("/reports/online-stock-groups", {
        params: periodStoreParams(params),
        timeout: 180_000,
    });

    return data;
}

export async function fetchOnlineStockGroupArticles(params: {
    groupCode: string;
    childCodes?: string[];
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}) {
    const { data } = await apiClient.get<{ groupCode: string; articles: RetailStockGroupNode[] }>(
        "/reports/online-stock-groups/articles",
        {
            params: {
                ...periodStoreParams(params),
                group_code: params.groupCode,
                ...(params.childCodes?.length ? { child_codes: params.childCodes.join(",") } : {}),
            },
            timeout: 180_000,
        },
    );

    return data;
}

/** Flow табът е скрит в Онлайн — празен stub за типове/експорт. */
export async function fetchOnlineFrequencyReport(_params: {
    period?: string;
    dateFrom?: string;
    dateTo?: string;
    store?: string;
}): Promise<RetailFrequencyResponse> {
    return {
        period: { label: "", from: "", to: "", days: 0 },
        store: ONLINE_STORE_LABEL,
        storeOptions: [ONLINE_STORE_LABEL],
        summary: {
            totalVisits: 0,
            uniqueCustomers: 0,
            avgVisitsPerCustomer: 0,
            avgDaysBetweenVisits: 0,
        },
        purchaseFrequency: [],
        purchaseFrequencyMeta: {
            chartCustomers: 0,
            chartVisits: 0,
            excluded: { customers: 0, visits: 0, items: [] },
        },
        flowSettings: { intervalMinutes: 60, hourFrom: 0, hourTo: 23 },
        hourlyFlow: [],
        dailyFlow: [],
        paymentMethods: [],
        lastUpdatedAt: new Date().toISOString(),
    };
}
